import type {ProductInfo} from '@globus/image-overlay';
import {storeToRefs} from 'pinia';
import {
	GsoaProductCatalogService,
	type Gsoa_ProductCatalog_ProductDetail as ProductDetail,
} from '~/openapi';
import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';

interface UseLeafletCatalogProductOptions {
	enabled?: MaybeRefOrGetter<boolean>;
}

export function useLeafletCatalogProduct(
	productInfo: MaybeRefOrGetter<ProductInfo | null | undefined>,
	options: UseLeafletCatalogProductOptions = {},
) {
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarketWithFallbackInstance} = storeToRefs(hypermarketsStore);

	const info = computed(() => toValue(productInfo));
	const enabled = computed(() => toValue(options.enabled ?? true));
	const houseNumber = computed(() => selectedHypermarketWithFallbackInstance.value?.content.gsoaId);
	const ean = computed(() => info.value?.EAN);

	const fetchParams = computed(() => {
		if (!enabled.value || !houseNumber.value || !ean.value) {
			return null;
		}

		return {
			houseNumber: Number(houseNumber.value),
			productId: ean.value,
		};
	});

	type FetchStatus = 'idle' | 'pending' | 'success' | 'error';

	const catalogProduct = ref<ProductDetail | null>(null);
	const status = ref<FetchStatus>('idle');

	async function fetchCatalogProduct() {
		const params = fetchParams.value;

		if (!params || !import.meta.client) {
			catalogProduct.value = null;
			status.value = 'idle';
			return;
		}

		status.value = 'pending';

		try {
			catalogProduct.value = await GsoaProductCatalogService.getHouseProductByEan(params) ?? null;
			status.value = 'success';
		} catch {
			catalogProduct.value = null;
			status.value = 'error';
		}
	}

	watch(fetchParams, () => fetchCatalogProduct(), {immediate: true});

	const pending = computed(() => {
		if (!enabled.value || !info.value || !ean.value) {
			return false;
		}

		return status.value === 'pending';
	});

	const shoppingListProduct = computed((): ProductDetail | undefined => {
		const product = info.value;
		if (!product || !enabled.value) {
			return undefined;
		}

		if (catalogProduct.value?.vanr) {
			return catalogProduct.value;
		}

		return {name: product.Name ?? ''} as ProductDetail;
	});

	return {
		catalogProduct,
		status,
		pending,
		shoppingListProduct,
	};
}
